import { useEffect, useState } from "react";
import { http } from "@/lib/api";

// In-memory cache for the site-wide special so we only hit /public/pricing once.
let _siteSpecialCache = undefined;
let _siteSpecialPromise = null;

/**
 * Returns the active site-wide special:
 *   { active, label, pct_off, expires_at }
 * or null if none is configured.
 */
export function useSiteSpecial() {
  const [s, setS] = useState(() => _siteSpecialCache);
  useEffect(() => {
    if (_siteSpecialCache !== undefined) return;
    if (!_siteSpecialPromise) {
      _siteSpecialPromise = http.get("/public/pricing")
        .then(({ data }) => { _siteSpecialCache = data.site_special || null; return _siteSpecialCache; })
        .catch(() => { _siteSpecialCache = null; return null; });
    }
    _siteSpecialPromise.then((v) => setS(v));
  }, []);
  return s;
}

/**
 * Returns the price breakdown for ONE variant, applying:
 *   1. artwork.special.sale_price (flat per-artwork sale — wins over %)
 *   2. site-wide special pct_off
 *
 * @returns {{ original: number, effective: number, isOnSale: boolean, pctOff: number, label: string }}
 */
export function computeSalePrice(originalPrice, artwork, siteSpecial) {
  const orig = Number(originalPrice) || 0;
  if (!orig) return { original: 0, effective: 0, isOnSale: false, pctOff: 0, label: "" };

  const artSpecial = artwork?.special;
  // Per-artwork flat sale price wins (only applies if it's less than the variant's price)
  if (artSpecial?.active && Number(artSpecial?.sale_price) > 0 && Number(artSpecial.sale_price) < orig) {
    const eff = Number(artSpecial.sale_price);
    const pct = Math.round(((orig - eff) / orig) * 100);
    return {
      original: orig,
      effective: eff,
      isOnSale: true,
      pctOff: pct,
      label: artSpecial.label || "ON SALE",
    };
  }
  // Per-artwork percentage off — applies to every variant of THIS artwork
  if (artSpecial?.active && Number(artSpecial?.pct_off) > 0) {
    const pct = Number(artSpecial.pct_off);
    const eff = Math.max(0, Math.round((orig * (1 - pct / 100)) * 100) / 100);
    return {
      original: orig,
      effective: eff,
      isOnSale: true,
      pctOff: pct,
      label: artSpecial.label || `${pct}% OFF`,
    };
  }
  // Site-wide percentage off
  if (siteSpecial?.active && Number(siteSpecial?.pct_off) > 0) {
    const pct = Number(siteSpecial.pct_off);
    const eff = Math.max(0, Math.round((orig * (1 - pct / 100)) * 100) / 100);
    return {
      original: orig,
      effective: eff,
      isOnSale: true,
      pctOff: pct,
      label: siteSpecial.label || "FLASH SALE",
    };
  }
  return { original: orig, effective: orig, isOnSale: false, pctOff: 0, label: "" };
}
